import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/api";

const CHECKOUT_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

const loadRazorpayCheckout = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Browser environment unavailable."));
      return;
    }

    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(`script[src="${CHECKOUT_SCRIPT_URL}"]`);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Razorpay SDK failed to load.")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = CHECKOUT_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Razorpay SDK failed to load."));
    document.body.appendChild(script);
  });

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";

  return amount.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
};

const formatStatus = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

export default function MyDomains() {
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [renewingId, setRenewingId] = useState(null);
  const [renewalMessage, setRenewalMessage] = useState("");
  const [renewalError, setRenewalError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login?redirect=/my-domains", { replace: true });
      return;
    }

    fetch(buildApiUrl("/api/my-domains"), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "Unable to load domains.");
        }

        setDomains(data.domains || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleRenew = async (domain) => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login?redirect=/my-domains", { replace: true });
      return;
    }

    setRenewingId(domain._id);
    setRenewalMessage("");
    setRenewalError("");

    try {
      await loadRazorpayCheckout();

      const orderRes = await fetch(buildApiUrl(`/api/domains/${domain._id}/renew/create-order`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const orderData = await orderRes.json();

      if (!orderRes.ok) {
        throw new Error(orderData.error || "Unable to create renewal payment.");
      }

      await new Promise((resolve, reject) => {
        const razorpay = new window.Razorpay({
          key: orderData.keyId,
          amount: Math.round(Number(orderData.amount) * 100),
          currency: orderData.currency || "INR",
          name: "Asiduo Solutions",
          description: `Domain renewal: ${domain.domain}`,
          order_id: orderData.orderId,
          handler: async (response) => {
            try {
              const verifyRes = await fetch(buildApiUrl(`/api/domains/${domain._id}/renew/verify`), {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  years: 1,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              });

              const verifyData = await verifyRes.json();

              if (!verifyRes.ok) {
                throw new Error(verifyData.error || "Payment verified, but renewal failed.");
              }

              if (verifyData.alreadyQueued) {
                setRenewalMessage("Renewal already queued.");
              } else if (verifyData.pending) {
                setRenewalMessage("Payment successful and renewal submitted.");
              } else {
                setRenewalMessage("Renewal completed.");
              }

              resolve(true);
            } catch (err) {
              reject(err);
            }
          },
          modal: {
            ondismiss: () => reject(new Error("Payment window closed.")),
          },
          theme: {
            color: "#0891b2",
          },
        });

        razorpay.on("payment.failed", (response) => {
          reject(new Error(response.error?.description || "Payment failed."));
        });

        razorpay.open();
      });
    } catch (err) {
      setRenewalError(err.message);
    } finally {
      setRenewingId(null);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-cyan-950 to-slate-900 px-6 py-28 text-white">
      <div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
      <div className="absolute bottom-10 right-10 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />

      <div className="mx-auto max-w-6xl">
        <div className="mb-10">
          <h1 className="text-5xl font-black leading-tight md:text-6xl">
            My
            <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              {" "}Domains
            </span>
          </h1>
          <p className="mt-4 text-lg text-slate-300">
            Track your purchased domains, registrar status, order details, and invoice details.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-cyan-500 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-400/30 bg-rose-500/10 p-6 text-rose-300">
            {error}
          </div>
        ) : domains.length === 0 ? (
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-10 text-center backdrop-blur-xl">
            <p className="text-2xl font-black text-slate-300">No domains purchased yet.</p>
            <button
              type="button"
              onClick={() => navigate("/domain")}
              className="mt-6 rounded-2xl bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-3 font-bold transition hover:scale-105"
            >
              Browse Domains
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-[28px] border border-white/10 bg-white/5 backdrop-blur-xl">
            {renewalMessage ? (
              <div className="border-b border-emerald-400/20 bg-emerald-500/10 px-5 py-3 text-sm font-semibold text-emerald-300">
                {renewalMessage}
              </div>
            ) : null}
            {renewalError ? (
              <div className="border-b border-rose-400/20 bg-rose-500/10 px-5 py-3 text-sm font-semibold text-rose-300">
                {renewalError}
              </div>
            ) : null}
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="border-b border-white/10 bg-white/5">
                  <tr>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">Domain Name</th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">Registration Status</th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">Order ID</th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">Invoice ID</th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">Expiry Date</th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">Purchase Date</th>
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-slate-300">Amount Paid</th>
                    <th className="px-5 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {domains.map((domain) => (
                    <tr key={domain._id} className="border-b border-white/5 last:border-b-0">
                      <td className="px-5 py-4 text-sm font-semibold text-white">{domain.domain || "-"}</td>
                      <td className="px-5 py-4 text-sm text-slate-200">{formatStatus(domain.registrarStatus)}</td>
                      <td className="px-5 py-4 text-sm font-mono text-slate-300">{domain.resellerClubOrderId || "-"}</td>
                      <td className="px-5 py-4 text-sm font-mono text-slate-300">{domain.resellerClubInvoiceId || "-"}</td>
                      <td className="px-5 py-4 text-sm text-slate-300">{formatDate(domain.expiryDate)}</td>
                      <td className="px-5 py-4 text-sm text-slate-300">{formatDate(domain.createdAt)}</td>
                      <td className="px-5 py-4 text-sm text-slate-200">{formatCurrency(domain.amountPaid)}</td>
                      <td className="px-5 py-4">
                        {domain.registrarStatus === "registered" ? (
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => navigate(`/my-domains/${domain._id}/dns`)}
                              className="rounded-xl border border-violet-400/30 bg-violet-500/10 px-4 py-2 text-xs font-bold text-violet-300 transition hover:bg-violet-500/20"
                            >
                              Manage DNS
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRenew(domain)}
                              disabled={renewingId === domain._id}
                              className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-xs font-bold text-cyan-300 transition hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {renewingId === domain._id ? "Renewing..." : "Renew Domain"}
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
